
import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { ExterneFreimeldung, InspectionItem, Contact, ProjectPlan, SafetyPoint, ReportLanguage, ProjektVerortung } from '../types';
import { Button } from './Button';
import { AIHelper } from './AIHelper';
import { generateSafetyText } from '../services/geminiService';
import { compressImage, HEIC_ACCEPT, bildFehlerText } from '../services/imageService';
import { uploadAsset } from '../services/cloudService';
import { istNativeApp } from '../services/nativePlatform';
import { useIsMobile } from '../hooks/useIsMobile';
import { X, CheckCircle, ImagePlus, Loader2, Camera } from 'lucide-react';
import { compareItemsForReportOrder } from '../services/pdf/pdfUtils';
import {
    freimeldeDatum,
    naechsteFreieTicketNummer,
    statusWechselAnwenden,
    ticketNummerFuerNeuenEintrag,
} from '../services/inspectionItemStatus';
import { freimeldungKommentar } from '../services/freimeldungLogic';
import { InspectionFilterBar } from './inspection-list/InspectionFilterBar';
import { InspectionItemCard } from './inspection-list/InspectionItemCard';
import { MangelLoeschenDialog } from './inspection-list/MangelLoeschenDialog';
import { pruefeLoeschen, type LoeschEntscheid } from '../services/maengelLoeschen';
import { useLiveStandort } from './verortung/useLiveStandort';
import { ortstextUebernehmen, verortungLesen } from '../services/verortung';
import { liveEbene, liveEintragVerortung, type LivePunkt } from '../services/verortung/liveStandort';
import { PlanVerortungDialog } from './plaene/PlanVerortungDialog';
import { pinEntfernen, pinHinzufuegen, pinsVon, planIdsVon, sensorPinSetzen, type PlanPin } from '../services/planPins';
import { MangelKatalogDialog } from './mangelKatalog/MangelKatalogDialog';
import { holdIdleExit, UPLOAD_HALTE, uploadBeginnt } from '../services/idleHold';
import { startPfadVon } from './mangelKatalog/auswahlAnsicht';
import { useMangelKatalog } from '../services/mangelKatalog/store';
import { katalogSprache } from '../services/mangelKatalog/sprache';
import { vorbelegungAus } from '../services/mangelKatalog/vorbelegung';
import { themenDerListe } from '../services/mangelKatalog/listenfilter';
import type { KatalogAuswahl } from '../services/mangelKatalog/typen';
import {
    KATALOG_NEU,
    katalogAktivFuer,
    katalogDialogTitel,
    katalogPatch,
    neuerEintragAusKatalog,
    themaFilterAnwenden,
    wirksamesThema,
} from './inspection-list/katalogListe';
import {
    beweisbildEinsetzen,
    beweisbildUrlAmEintrag,
    beweisbildUrlNachtragen,
    fotosHochladenUndAnhaengen,
    kiRueckgaengigMoeglich,
    kiVorschlagUebernehmen,
    spaetSchreiben,
    type ItemsAendern,
    type KiRueckgaengig,
    type SchreibWeg,
} from './inspection-list/listeLogik';
import { useNetzDa, type KartenUmgebung } from './inspection-list/kartenUmgebung';
import {
    KARTEN_SEITE,
    fensterAnzahlFuer,
    fensterLage,
    restKarten,
    sichtbareKarten,
    type KartenFenster,
} from './inspection-list/kartenFenster';

// Feste leere Listen: `|| []` beim Aufrufer baut bei jedem Render eine neue —
// jede Karte saehe dann eine „geaenderte“ Liste (React.memo).
const KEINE_KONTAKTE: Contact[] = [];
const KEINE_PLAENE: ProjectPlan[] = [];

interface Props {
  items: InspectionItem[];
  onChange: (items: InspectionItem[]) => void;
  contacts: Contact[];
  plans: ProjectPlan[];
  planImage?: string | null;
  safetyPoints?: SafetyPoint[];
  filterMode: 'active' | 'history';
  language?: ReportLanguage;
  appMode?: 'sigeko' | 'hse';
  activeSectionId?: string | null; 
  reportDate?: string; 
  projectId?: string;
  // Mobil: startet den Erfassungs-Wizard (statt der leeren Desktop-Karte)
  onMobileAdd?: () => void;
  /**
   * Die OFFENEN Online-Freimeldungen je Mangel-Id (`offeneJeItem` aus
   * useFreimeldungen). Ohne die Karte: Maengel ohne Meldung fehlen in der Map
   * ganz, `get` liefert dann `undefined`.
   */
  freimeldungen?: Map<string, ExterneFreimeldung[]>;
  /** Bucht die Entscheidung des Koordinators SOFORT (RPC). Fehlt der Handler, gibt es keine Knöpfe. */
  onFreimeldungEntscheiden?: (id: string, entscheidung: 'bestaetigt' | 'abgelehnt', kommentar?: string) => Promise<void>;
  /**
   * Reiht eine Entscheidung ein, die erst gebucht werden darf, wenn der Mangel
   * NACHWEISLICH nicht mehr offen in der Datenbank steht
   * (docs/freimeldung-online-plan.md §F). Das Begehungsmodul fragt dafuer die
   * Zeile ab; die `itemId` gehoert deshalb zwingend dazu.
   *
   * Drei Aufrufer: das Abschließen des Freimelde-Dialogs (dort wird der Mangel
   * im Client geschlossen und muss erst hochgeladen werden), „Zur Kenntnis
   * genommen" an einem bereits erledigten Mangel — auch dessen Erledigung kann
   * rein lokal sein und noch in keinem Save stecken — und die Abkuerzung fuer
   * einen nachtraeglich auf „Information" gestellten Mangel, der `closed` nie
   * erreicht und serverseitig als `sonstig` nachgewiesen wird.
   *
   * Fehlt der Handler (InspectionList ausserhalb des Begehungsmoduls), fallen
   * das Abschließen und die Information-Abkuerzung auf
   * `onFreimeldungEntscheiden` zurueck; „Zur Kenntnis genommen" entfaellt dann
   * ganz — ohne Nachweis-Weg gibt es diesen Knopf nicht mehr.
   */
  onFreimeldungNachSpeichern?: (id: string, itemId: string, entscheidung: 'bestaetigt' | 'abgelehnt', kommentar?: string) => void;
  /** Klartext der letzten gescheiterten Entscheidung — steht an der Mangelkarte. */
  freimeldungFehler?: string | null;
  /**
   * Sensor-Verortung des Projekts (`metadata.verortung`, roh): in der App zeigt
   * „Verortung auf Plan“ dann den Live-Standort (blinkender Punkt mit Blickrichtung).
   * Abschaltbar ueber Geraet, Projekt und „Live aus“ (docs/verortung-kamera-vertrag.md).
   */
  verortung?: ProjektVerortung;
  /**
   * Neu hochgeladener Plan aus „Verortung auf Plan“ (Knopf „+ Plan“ bzw.
   * „Plan hochladen“). Der Aufrufer haengt ihn an `metadata.plans` an. Ohne
   * Handler (oder ohne `projectId`) gibt es im Dialog kein Hochladen.
   */
  onPlanAnlegen?: (plan: ProjectPlan) => void;
  /**
   * Spaete Ergebnisse (Foto-Upload, KI-Vorschlag, URL des Beweisbilds) als
   * Aenderung auf den Stand, der bei der Antwort gilt. Der Aufrufer ruft
   * `aendern` sofort mit den Eintraegen seines aktuellen Berichts auf — das
   * wirkt auch, wenn die Liste inzwischen abgebaut ist (Assistent offen,
   * Reiterwechsel). Ohne Handler schreibt die Liste selbst, aber nur, solange
   * sie steht.
   */
  onItemsAendern?: ItemsAendern;
}

export const InspectionList: React.FC<Props> = ({
  items, onChange, contacts, plans, filterMode, language = 'de', appMode = 'sigeko', activeSectionId, reportDate, projectId, onMobileAdd,
  freimeldungen, onFreimeldungEntscheiden, onFreimeldungNachSpeichern, freimeldungFehler = null, verortung, onPlanAnlegen,
  onItemsAendern
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  
  // Modals States
  const [isAiHelperOpen, setIsAiHelperOpen] = useState(false);
  const [aiInitialText, setAiInitialText] = useState(''); 
  // „Verortung auf Plan“: Id des Eintrags, dessen Nadeln gerade gesetzt werden
  // (null = zu). Der Eintrag selbst kommt bei jedem Render frisch aus `items`.
  const [verortungItemId, setVerortungItemId] = useState<string | null>(null);
  const verortungItem = verortungItemId ? items.find(i => i.id === verortungItemId) : undefined;
  // Live-Standort in „Verortung auf Plan“ (nur App mit Sensoren; sonst unsichtbar).
  const projektVerortung = useMemo(() => verortungLesen(verortung), [verortung]);
  const live = useLiveStandort({
      offen: !!verortungItem,
      verortung: projektVerortung,
      plaene: plans,
      sitzungSchluessel: projectId || 'ohne-projekt',
  });
  // `freimeldungId` traegt den Dialog nur, wenn er AUS einer Online-Freimeldung
  // heraus geoeffnet wurde: dann — und nur dann — wird die Meldung nach dem
  // Schliessen als bestaetigt gebucht. Der normale Weg ueber „Erledigt" setzt
  // ein frisches Objekt ohne dieses Feld.
  // `sitzung`: jede Oeffnung des Dialogs eine neue Kennung — ein spaetes
  // Beweisbild landet nur in der Oeffnung, in der es gewaehlt wurde.
  const [closeModal, setCloseModal] = useState<{ isOpen: boolean; sitzung: number; itemId: string; date: string; comment: string; image?: string; freimeldungId?: string }>({ isOpen: false, sitzung: 0, itemId: '', date: '', comment: '' });
  const dialogZaehler = useRef(0);
  const neueDialogSitzung = useCallback(() => (dialogZaehler.current += 1), []);
  const closeImageInputRef = useRef<HTMLInputElement>(null);
  // Am Handy und in der App: Kamera UND Album — ohne `capture` oeffnet die App nur die Galerie.
  const closeKameraInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const kameraUndAlbum = isMobile || istNativeApp();
  // Dialog-Oeffnung, deren Beweisbild gerade verkleinert wird (Platzhalter, „Abschließen“ wartet).
  const [beweisbildLaedtFuer, setBeweisbildLaedtFuer] = useState<number | null>(null);
  const beweisbildLaedt = closeModal.isOpen && beweisbildLaedtFuer === closeModal.sitzung;
  // Einstufiges „Rückgängig“ nach dem KI-Vorschlag an einer Karte.
  const [kiRueckgaengig, setKiRueckgaengig] = useState<KiRueckgaengig | null>(null);

  // Ref statt Closure: Hochladen und KI-Anfrage dauern Sekunden, zwischenzeitliche
  // Edits am Item duerfen beim Anwenden nicht ueberschrieben werden. Dasselbe
  // gilt fuer `onChange` — der Rueckruf eines alten Renders traegt einen alten Bericht.
  // Auch die festen Karten-Handler (React.memo, unten) lesen Eintraege und
  // Rueckrufe erst beim Aufruf hier. Nachgezogen im Layout-Effekt: direkt nach
  // dem Commit, vor jedem Klick oder Tastendruck auf den neuen Stand.
  const itemsRef = useRef(items);
  const onChangeRef = useRef(onChange);
  const onItemsAendernRef = useRef(onItemsAendern);
  const onFreimeldungEntscheidenRef = useRef(onFreimeldungEntscheiden);
  const onFreimeldungNachSpeichernRef = useRef(onFreimeldungNachSpeichern);
  const projectIdRef = useRef(projectId);
  useLayoutEffect(() => {
      itemsRef.current = items;
      onChangeRef.current = onChange;
      projectIdRef.current = projectId;
      onItemsAendernRef.current = onItemsAendern;
      onFreimeldungEntscheidenRef.current = onFreimeldungEntscheiden;
      onFreimeldungNachSpeichernRef.current = onFreimeldungNachSpeichern;
  });
  // Abgebaut traegt `itemsRef` einen alten Stand: ohne `onItemsAendern` wird dann nicht mehr geschrieben.
  const stehtRef = useRef(false);
  useEffect(() => {
      stehtRef.current = true;
      return () => { stehtRef.current = false; };
  }, []);
  // Schreibweg fuer spaete Ergebnisse — erst bei der Antwort gelesen, nie beim Start.
  const schreibWeg = useCallback((): SchreibWeg => ({
      aendern: onItemsAendernRef.current,
      items: itemsRef.current,
      melden: onChangeRef.current,
      steht: stehtRef.current,
  }), []);

  // Mängelkatalog „Standardmangel per Torte“ (in der Katalogsprache: englisch
  // nur bei 'en'): Dialog-Ziel KATALOG_NEU (Knopf „Standardmangel“) oder die
  // ID des Eintrags (Knopf „Standard“ an der Karte); null = zu. Dazu der Filter „Thema“.
  const erfassungsSprache = katalogSprache(language);
  const katalog = useMangelKatalog(erfassungsSprache);
  const katalogAktiv = katalogAktivFuer(language);
  // Mobil/Netz/Katalog EINMAL fuer alle Karten (kartenUmgebung) — nicht je Karte.
  const online = useNetzDa();
  const kartenUmgebung = useMemo<KartenUmgebung>(() => ({ isMobile, online, katalog }), [isMobile, online, katalog]);
  const [katalogZiel, setKatalogZiel] = useState<string | null>(null);
  // Filter „Thema“ samt den Einträgen, die es beim Setzen schon gab (themaFilterAnwenden).
  const [themaWahl, setThemaWahl] = useState<{ id: string; bekannt: ReadonlySet<string> } | null>(null);
  const themaFilter = themaWahl?.id ?? null;
  const setThemaFilter = (id: string | null) => setThemaWahl(id ? { id, bekannt: new Set(items.map(i => i.id)) } : null);

  // Modus + Abschnitt: Grundlage der Liste UND der Themen im Filter „Thema“.
  const ansichtItems = useMemo(() => items.filter(item => {
       const isClosed = item.status === 'closed';
       const modeMatch = filterMode === 'history' ? isClosed : !isClosed;
       const sectionMatch = (!activeSectionId || activeSectionId === 'all') ? true : item.sectionId === activeSectionId;
       return modeMatch && sectionMatch;
  }), [items, filterMode, activeSectionId]);

  const themen = useMemo(() => (katalogAktiv ? themenDerListe(ansichtItems, katalog) : []), [katalogAktiv, ansichtItems, katalog]);
  // Verschwindet das Thema aus der Liste, gilt wieder „alle“ (abgeleitet, kein setState im Effekt).
  const wirksamerThemaFilter = wirksamesThema(themen, themaFilter);

  // Filter & Sort Logic
  const filteredItems = useMemo(() => {
    // Kopie: sort() unten sortiert an Ort und Stelle.
    let result = themaFilterAnwenden(ansichtItems, wirksamerThemaFilter, themaWahl?.bekannt ?? null);

    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => 
            (i.title || '').toLowerCase().includes(lower) || 
            (i.location || '').toLowerCase().includes(lower) ||
            (i.ticketNumber?.toString() || '').includes(lower)
        );
    }

    if (statusFilter !== 'all' && filterMode === 'active') {
        result = result.filter(i => i.status === statusFilter);
    }

    // SORTIERUNG: exakt dieselbe Funktion wie das PDF — Ansicht und Ausdruck
    // dürfen nicht auseinanderlaufen. Der Filter oben ist ein anderer (die
    // Liste zeigt auch „Erledigt"), deshalb nur der Vergleich, nicht
    // selectActiveItemsForReport.
    return result.sort(compareItemsForReportOrder);
  }, [ansichtItems, wirksamerThemaFilter, themaWahl, filterMode, searchTerm, statusFilter]);

  // --- Seitenweise Anzeige (kartenFenster) ---
  // Die Historie eines grossen Projekts traegt ~600 Karten. Gesucht und
  // gefiltert wird oben ueber ALLE; hier werden erst die ersten 50 gezeigt.
  // Neue Filterlage (Modus, Abschnitt, Suche, Status, Thema) → wieder 50.
  const lage = fensterLage([filterMode, activeSectionId, searchTerm, statusFilter, wirksamerThemaFilter]);
  const [fenster, setFenster] = useState<KartenFenster>(() => ({ lage, anzahl: KARTEN_SEITE }));
  if (fenster.lage !== lage) {
      // Zustand aus dem vorigen Render angleichen (kein Effekt): auch A → B → A beginnt wieder bei 50.
      setFenster({ lage, anzahl: KARTEN_SEITE });
  }
  const fensterAnzahl = fensterAnzahlFuer(fenster, lage);
  // Neu angelegte (und zum Vervollstaendigen aufgeklappte) Eintraege bleiben
  // stehen, auch hinter dem Fenster — wie die gerade aufgeklappte Karte.
  const [immerSichtbar, setImmerSichtbar] = useState<ReadonlySet<string>>(() => new Set());
  const aufklappenUndZeigen = (id: string) => {
      setExpandedItem(id);
      setImmerSichtbar(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  };
  const sichtbareAnzahl = sichtbareKarten(filteredItems, fensterAnzahl, id => id === expandedItem || immerSichtbar.has(id));
  const { weitere: weitereKarten, rest: restlicheKarten } = restKarten(filteredItems.length, sichtbareAnzahl);

  // Actions
  const handleAddItem = () => {
      // VALIDATION: Check for incomplete items before adding new one
      // Updated to check Location & Responsible Person if not General status
      const incompleteItem = items.find(i => {
          if (i.status === 'closed') return false;
          // Informationen brauchen nur Überschrift und Text — kein Ort, keine Firma
          if (i.status === 'general') return !i.title?.trim() || !i.observation?.trim();

          // Regular items need all 4 fields
          return !i.title?.trim() || !i.observation?.trim() || !i.location?.trim() || !i.responsiblePerson?.trim();
      });

      if (incompleteItem) {
          alert(incompleteItem.status === 'general'
              ? "Erst Vervollständigen: Bitte füllen Sie Überschrift und Text der Information aus."
              : "Erst Vervollständigen: Bitte füllen Sie alle Pflichtfelder (Titel, Beschreibung, Ort, Firma) für den offenen Mangel aus.");
          aufklappenUndZeigen(incompleteItem.id);
          // Scroll to item ideally, but setting expanded usually brings it into view or user attention
          return;
      }

      const sectionFilter = (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined;
      // Dieselbe Datumsregel wie die Freimeldung: Begehungstag, normalisiert.
      const defaultDate = freimeldeDatum(reportDate);

      const newItem: InspectionItem = {
          id: crypto.randomUUID(),
          // Fortlaufende Nummerierung über den GESAMTEN Bestand (unabhängig von
          // Abschnitt und Status). Neu ist immer ein Mangel, also immer mit
          // Nummer — zur Information wird der Eintrag erst per Kachelwahl,
          // und DANN gibt er seine Nummer wieder frei.
          ticketNumber: ticketNummerFuerNeuenEintrag('open', naechsteFreieTicketNummer(items)),
          title: '',
          location: '',
          observation: '',
          regulation: '',
          responsiblePerson: '',
          deadline: defaultDate,
          dateFound: defaultDate,
          status: 'open',
          photos: [],
          sectionId: sectionFilter
      };
      
      onChange([newItem, ...items]);
      aufklappenUndZeigen(newItem.id);
  };

  // Die Handler an den Karten sind FEST (useCallback): jede Karte ist
  // React.memo und rendert nur neu, wenn sich IHR Eintrag aendert. Eintraege
  // und Rueckrufe lesen sie deshalb erst beim Aufruf aus den Refs oben —
  // nie aus einem alten Render.
  const handleUpdateItem = useCallback((id: string, field: keyof InspectionItem, value: any) => {
      const newItems = itemsRef.current.map(i => i.id === id ? { ...i, [field]: value } : i);
      onChangeRef.current(newItems);
  }, []);

  const handlePatchItem = useCallback((id: string, patch: Partial<InspectionItem>) => {
      onChangeRef.current(itemsRef.current.map(i => i.id === id ? { ...i, ...patch } : i));
  }, []);

  // Löschen nur, was noch nicht beim Kunden ist (Betreiber 06.10.2026,
  // services/maengelLoeschen.ts): steht der Eintrag im letzten archivierten
  // Bericht als offen, bietet der Dialog „erledigt“ statt Löschen an.
  const [loeschen, setLoeschen] = useState<{ id: string; entscheid: LoeschEntscheid | null } | null>(null);
  // Jeder Schritt (Öffnen, Abbrechen, Erledigt, Verwerfen) ist ein neuer
  // Vorgang; eine Archivprüfung, die zurückkommt, wenn ihr Vorgang nicht mehr
  // der aktuelle ist, wirkt nicht mehr — sonst löschte ein abgebrochener Dialog.
  const loeschVorgangRef = useRef(0);
  const handleDeleteItem = useCallback((id: string) => {
      const item = itemsRef.current.find(i => i.id === id);
      if (!item) return;
      const vorgang = ++loeschVorgangRef.current;
      setLoeschen({ id, entscheid: null });
      void pruefeLoeschen(item, projectIdRef.current).then((entscheid) => {
          if (loeschVorgangRef.current === vorgang) setLoeschen({ id, entscheid });
      });
  }, []);
  const loeschenAbbrechen = useCallback(() => {
      loeschVorgangRef.current += 1;
      setLoeschen(null);
  }, []);
  // Vor dem Verwerfen FRISCH prüfen (Codex-Review 06.10.): zwischen Öffnen
  // und Klick kann der Bericht versendet worden sein. Verworfen wird nur, wenn
  // die neue Prüfung es erlaubt — oder wenn sie wieder nichts feststellen kann
  // und der Nutzer schon „Trotzdem verwerfen“ gewählt hatte.
  const loeschenVerwerfen = useCallback(async (id: string, warUnbekannt: boolean) => {
      const item = itemsRef.current.find(i => i.id === id);
      const vorgang = ++loeschVorgangRef.current;
      if (!item) { setLoeschen(null); return; }
      setLoeschen({ id, entscheid: null });
      const neu = await pruefeLoeschen(item, projectIdRef.current);
      if (loeschVorgangRef.current !== vorgang) return;
      if (neu.art === 'verwerfen' || (neu.art === 'unbekannt' && warUnbekannt)) {
          setLoeschen(null);
          onChangeRef.current(itemsRef.current.filter(i => i.id !== id));
      } else {
          setLoeschen({ id, entscheid: neu });
      }
  }, []);

  const handleStatusChangeTrigger = useCallback((id: string, newStatus: 'open' | 'closed' | 'info' | 'good' | 'general') => {
      // Stand beim Klick (Refs), nicht der des Renders, in dem der Handler entstand.
      const items = itemsRef.current;
      const onChange = onChangeRef.current;
      if (newStatus === 'closed') {
          const item = items.find(i => i.id === id);
          // Eine Information ist eine Vorbemerkung an den Leser, keine Aufgabe —
          // sie kann nicht erledigt werden. Die Kachel „Erledigt" ist fuer sie
          // bereits ausgeblendet; dieser Riegel haelt die Regel auch fuer jeden
          // anderen Aufrufer. Ohne ihn vergaebe der Statuswechsel dem
          // nummernlosen Eintrag eine laufende Nummer, die beim Zurueckstellen
          // auf „Information" verbrannt waere.
          if (item && item.status === 'general') return;
          // Positiv ist mit der Begehung abgeschlossen: sofort schliessen, ohne
          // den Freimelde-Dialog (es gibt nichts zu kommentieren oder zu belegen).
          if (item && item.status === 'good') {
             onChange(statusWechselAnwenden(items, id, 'closed', reportDate));
             return;
          }

          // Vorbelegung, kein Zwang: der Dialog laesst das Datum weiter aendern.
          setCloseModal({ isOpen: true, sitzung: neueDialogSitzung(), itemId: id, date: freimeldeDatum(reportDate), comment: 'Mangel wurde behoben.', image: undefined });
          return;
      }
      onChange(statusWechselAnwenden(items, id, newStatus, reportDate));
  }, [reportDate, neueDialogSitzung]);

  // --- Online-Freimeldung ---
  const freimeldeSprache = language === 'en' ? 'en' : 'de';

  /**
   * „Bestätigen" an einer Online-Freimeldung: oeffnet denselben Freimelde-
   * Dialog wie der Knopf „Erledigt", nur vorbelegt mit dem, was die Firma
   * gemeldet hat — Datum der Begehung, Kommentar mit Firma/Name/Tag, Beweisbild.
   *
   * Der Koordinator kann alles noch aendern und auch abbrechen: die Meldung
   * wird erst gebucht, wenn der Dialog wirklich abgeschlossen wird
   * (confirmCloseItem). Andersherum stuende die Meldung auf „bestaetigt" und
   * der Mangel weiter offen.
   */
  const openCloseFromFreimeldung = useCallback((f: ExterneFreimeldung) => {
      // Rueckrufe beim Klick (Refs), nicht die des Renders, in dem der Handler entstand.
      const onFreimeldungNachSpeichern = onFreimeldungNachSpeichernRef.current;
      const onFreimeldungEntscheiden = onFreimeldungEntscheidenRef.current;
      // Derselbe Riegel wie in handleStatusChangeTrigger: eine Information ist
      // eine Vorbemerkung an den Leser, keine Aufgabe — sie kann nicht
      // erledigt werden. Wurde ein bereits versendeter Mangel nachtraeglich
      // auf „Information" gestellt, hat er seine laufende Nummer freigegeben;
      // ueber den Dialog bekaeme er beim Schliessen eine frische Nummer
      // verpasst. Die Meldung darf trotzdem nicht haengen bleiben: sie wird
      // wie bei einem bereits geschlossenen Mangel zur Kenntnis genommen.
      //
      // AUCH DIESE ABKUERZUNG GEHT UEBER DIE WARTESCHLANGE. Sie stuetzte sich
      // frueher auf den LOKALEN Status und buchte sofort — die Herabstufung auf
      // „Information" steckte zu diesem Zeitpunkt aber oft in keinem Save: der
      // laeuft entprellt hinterher und kann scheitern. Dann stand die Meldung
      // auf „bestaetigt" (und mit ihr, ueber die RPC, alle Geschwister
      // desselben Mangels), waehrend der Mangel serverseitig weiter offen war.
      // Nachgewiesen wird hier nicht `closed` — das erreicht eine Information
      // nie —, sondern `sonstig`: der Mangel ist serverseitig keine Aufgabe
      // mehr. Ohne den Handler (InspectionList ausserhalb des Begehungsmoduls)
      // bleibt der alte, sofortige Weg.
      const eintrag = itemsRef.current.find(i => i.id === f.itemId);
      if (eintrag && eintrag.status === 'general') {
          if (onFreimeldungNachSpeichern) {
              onFreimeldungNachSpeichern(f.id, f.itemId, 'bestaetigt');
          } else if (onFreimeldungEntscheiden) {
              void onFreimeldungEntscheiden(f.id, 'bestaetigt');
          }
          return;
      }
      setCloseModal({
          isOpen: true,
          sitzung: neueDialogSitzung(),
          itemId: f.itemId,
          date: freimeldeDatum(reportDate),
          comment: freimeldungKommentar(f, freimeldeSprache),
          image: f.fotoUrl ?? '',
          freimeldungId: f.id,
      });
  }, [reportDate, freimeldeSprache, neueDialogSitzung]);

  // „Ablehnen" bleibt sofort: es schliesst nichts, es gibt also auch nichts
  // nachzuweisen.
  const freimeldungAblehnen = useCallback((f: ExterneFreimeldung, kommentar: string) => {
      const entscheiden = onFreimeldungEntscheidenRef.current;
      if (entscheiden) void entscheiden(f.id, 'abgelehnt', kommentar);
  }, []);

  // „Zur Kenntnis genommen" steht an einem Mangel, der HIER schon erledigt
  // aussieht — das kann aber allein im Speicher so sein, wenn er gerade
  // geschlossen und noch nicht hochgeladen wurde. Deshalb laeuft auch dieser
  // Weg ueber die Warteschlange und wartet auf den Nachweis (Abzeichen bleibt
  // bis dahin stehen).
  const freimeldungKenntnis = useCallback((f: ExterneFreimeldung) => {
      onFreimeldungNachSpeichernRef.current?.(f.id, f.itemId, 'bestaetigt');
  }, []);

  // --- Photo Handling ---
  // Auf den Stand bei der ANTWORT: nur die Fotos kommen dazu, Eingaben und
  // Eintraege von waehrend des Uploads bleiben stehen.
  // Bis die Fotos am Eintrag stehen, liegen sie nur hier: kein Neuladen
  // (Update-Balken, Deployment) und kein Leerlauf-Rueckzug dazwischen —
  // hoechstens zwei Minuten, ein haengender Upload sperrt sonst dauerhaft.
  const handleAddPhotos = useCallback((id: string, base64s: string[]) => {
      const loesen = holdIdleExit(UPLOAD_HALTE.foto);
      const obergrenze = window.setTimeout(loesen, 120_000);
      // Senden/Verwerfen im Modul bleiben bis zum echten Ende gesperrt (uploadLaeuft).
      const uploadEnde = uploadBeginnt();
      return fotosHochladenUndAnhaengen(id, base64s, projectId ? (base64) => uploadAsset(base64, projectId) : null, schreibWeg)
          .finally(() => {
              window.clearTimeout(obergrenze);
              loesen();
              uploadEnde();
          });
  }, [projectId, schreibWeg]);

  const handleRemovePhoto = useCallback((id: string, index: number) => {
      onChangeRef.current(itemsRef.current.map(i => {
          if (i.id === id) {
              const newPhotos = [...i.photos];
              newPhotos.splice(index, 1);
              return { ...i, photos: newPhotos };
          }
          return i;
      }));
  }, []);

  // --- AI ---
  // Scheitert die KI, wirft generateSafetyText mit anzeigbarer Meldung: die
  // Karte zeigt sie, die Felder bleiben unveraendert.
  const handleAiAssistDirect = useCallback(async (id: string, text: string, shortVersion: boolean) => {
      const rueckgaengig = await kiVorschlagUebernehmen(
          id,
          () => generateSafetyText(text, shortVersion, language as ReportLanguage, appMode as 'sigeko' | 'hse'),
          schreibWeg,
      );
      if (rueckgaengig) setKiRueckgaengig(rueckgaengig);
  }, [language, appMode, schreibWeg]);

  // Nur an der einen Karte, die noch den KI-Text traegt (kiRueckgaengigMoeglich).
  const kiRueckgaengigAnwenden = useCallback(() => {
      if (!kiRueckgaengig) return;
      handlePatchItem(kiRueckgaengig.id, kiRueckgaengig.vorher);
      setKiRueckgaengig(null);
  }, [kiRueckgaengig, handlePatchItem]);

  // Auf-/Zuklappen per Id (die Karte reicht ihre Id): ein Handler fuer alle Karten.
  const aufklappenUmschalten = useCallback((id: string) => {
      setExpandedItem(prev => (prev === id ? null : id));
  }, []);

  // „Verortung auf Plan“: immer oeffnen — auch ohne Plan (dann mit „Plan hochladen“ bzw. Hinweis).
  const aufPlanVerorten = useCallback((id: string) => setVerortungItemId(id), []);

  // Knopf „Standard“ an der Karte: Katalog-Dialog fuer diesen Eintrag.
  const katalogFuerEintrag = useCallback((id: string) => setKatalogZiel(id), []);

  const handleAIApply = (data: { title: string; observation: string; regulation: string }) => {
      if (expandedItem) {
          const item = items.find(i => i.id === expandedItem);
          if (item) {
              onChange(items.map(i => i.id === expandedItem ? { ...i, title: data.title, observation: data.observation, regulation: data.regulation } : i));
          }
      } else {
          // New item from AI
          const sectionFilter = (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined;
          const defaultDate = freimeldeDatum(reportDate);

          const newItem: InspectionItem = {
              id: crypto.randomUUID(),
              // Fortlaufende Nummerierung auch für KI-generierte Mängel
              ticketNumber: ticketNummerFuerNeuenEintrag('open', naechsteFreieTicketNummer(items)),
              title: data.title,
              location: '',
              observation: data.observation,
              regulation: data.regulation,
              responsiblePerson: '',
              deadline: defaultDate,
              dateFound: defaultDate,
              status: 'open',
              photos: [],
              sectionId: sectionFilter
          };
          onChange([newItem, ...items]);
          aufklappenUndZeigen(newItem.id);
      }
      setIsAiHelperOpen(false);
  };

  // --- Standardmangel (Katalog) ---
  // Der Dialog schließt nach der Auswahl NICHT selbst: das Ziel wird hier geleert.
  const handleKatalogAuswahl = (a: KatalogAuswahl) => {
      const ziel = katalogZiel;
      setKatalogZiel(null);
      if (!ziel) return;
      const vorbelegung = vorbelegungAus(a, new Date());
      if (ziel === KATALOG_NEU) {
          // Neuanlage wie beim Textvorschlag (handleAIApply), Frist ohne Dringlichkeit = Begehungstag.
          const newItem = neuerEintragAusKatalog({
              id: crypto.randomUUID(),
              items,
              vorbelegung,
              datum: freimeldeDatum(reportDate),
              activeSectionId,
          });
          onChange([newItem, ...items]);
          aufklappenUndZeigen(newItem.id);
          // Sonst verschwände der neue Eintrag hinter einem anderen Thema.
          if (wirksamerThemaFilter && wirksamerThemaFilter !== newItem.katalogKategorie) setThemaFilter(null);
          return;
      }
      handlePatchItem(ziel, katalogPatch(vorbelegung));
  };

  // „Standard“ an der Karte startet in der Ebene des bisherigen Standardmangels.
  const katalogStartPfad = useMemo(() => {
      if (!katalogZiel || katalogZiel === KATALOG_NEU) return undefined;
      const eintrag = items.find(i => i.id === katalogZiel);
      return eintrag ? startPfadVon(katalog, { katalogKey: eintrag.katalogKey, katalogKategorie: eintrag.katalogKategorie }) : undefined;
  }, [katalogZiel, items, katalog]);

  // --- Verortung auf Plan (docs/planverortung-vertrag.md) ---
  // Nadeln nur ueber services/planPins.ts: jeder Tipp setzt eine WEITERE
  // Nadel, auch auf einem anderen Plan — ein Planwechsel ersetzt nichts mehr.
  const pinSetzen = (pin: PlanPin) => {
      const plan = plans.find(p => p.id === pin.planId);
      onChange(items.map(i => {
          if (i.id !== verortungItemId) return i;
          const location = (!i.location || i.location.trim() === '') && plan ? plan.name : i.location;
          return {
              ...i,
              ...pinHinzufuegen(i, pin),
              location,
              // `verortung` (z. B. nur Geschoss) bleibt: die Sensor-Nadel ist
              // ausdruecklich markiert, eine Hand-Nadel wird nie dazu (planPins).
          };
      }));
  };

  // Faellt die Sensor-Nadel weg, gehoert der Blickpfeil zu keiner Nadel mehr (pinEntfernen).
  const pinEntfernenAmEintrag = (index: number) => {
      onChange(items.map(i => (i.id === verortungItemId ? { ...i, ...pinEntfernen(i, index) } : i)));
  };

  /** Live-Standort als Sensor-Nadel uebernehmen (ersetzt nur die alte Sensor-Nadel, Hand-Nadeln bleiben). */
  const liveUebernehmen = (planId: string, punkt: LivePunkt) => {
      const selectedPlan = plans.find(p => p.id === planId);
      // Der gewaehlte Plan bestimmt das Geschoss (Codex-Review 27.09., P2).
      const ebene = liveEbene(projektVerortung, planId, live.schaetzung);
      onChange(items.map(i => (i.id === verortungItemId
          ? {
              ...i,
              ...sensorPinSetzen(i, { x: punkt.pin.x, y: punkt.pin.y, planId }),
              location: ortstextUebernehmen(i.location || '', ebene) || selectedPlan?.name || i.location,
              verortung: liveEintragVerortung(punkt, live.schaetzung, ebene),
          }
          : i)));
      live.planBestaetigen(planId);
  };

  // Gemerkt und leer immer dieselbe Liste: sie geht an jede Karte (React.memo).
  const availablePlans = useMemo(() => {
      const imAbschnitt = plans.filter(p => !activeSectionId || activeSectionId === 'all' || !p.sectionId || p.sectionId === activeSectionId);
      return imAbschnitt.length > 0 ? imAbschnitt : KEINE_PLAENE;
  }, [plans, activeSectionId]);
  const kartenKontakte = contacts.length > 0 ? contacts : KEINE_KONTAKTE;
  // Im Dialog: die Plaene des Abschnitts plus jeder Plan, auf dem der Eintrag schon Nadeln traegt.
  const verortungPlaene = verortungItem
      ? plans.filter(p => availablePlans.includes(p) || planIdsVon(verortungItem).includes(p.id))
      : availablePlans;
  const planAnlegen = onPlanAnlegen && projectId
      ? { projectId, sectionId: activeSectionId && activeSectionId !== 'all' ? activeSectionId : undefined, onAngelegt: onPlanAnlegen }
      : undefined;

  // --- Close Modal Logic ---
  const handleCloseImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      const { itemId, sitzung } = closeModal;
      setBeweisbildLaedtFuer(sitzung);
      const verkleinert = await compressImage(file, 800, 0.55).catch((err: unknown) => {
          console.error('Clearance image upload error', err);
          alert(bildFehlerText(err, language === 'en' ? 'en' : 'de',
              language === 'en' ? 'Error processing image.' : 'Fehler beim Verarbeiten des Bildes.'));
          return null;
      });
      setBeweisbildLaedtFuer(prev => (prev === sitzung ? null : prev));
      if (verkleinert === null) return;
      // Sofort in den Dialog (funktional: Datum/Kommentar von zwischendurch
      // bleiben) — „Abschließen“ wartet nicht aufs Netz. Das verkleinerte Bild
      // ist der bestehende Rueckfall eines gescheiterten Uploads; der
      // Speicherweg laedt es selbst hoch.
      setCloseModal(prev => beweisbildEinsetzen(prev, sitzung, verkleinert));
      if (!projectId) return;
      const url = await uploadAsset(verkleinert, projectId).catch((err: unknown) => {
          console.error("Upload failed, falling back to base64", err);
          return verkleinert;
      });
      // URL nachtragen: im Dialog, solange er genau dieses Bild zeigt — oder,
      // wurde schon abgeschlossen, am Eintrag. (Kam nur das Base64 zurueck,
      // aendern beide nichts.)
      setCloseModal(prev => beweisbildUrlNachtragen(prev, sitzung, verkleinert, url));
      spaetSchreiben(schreibWeg(), aktuell => beweisbildUrlAmEintrag(aktuell, itemId, verkleinert, url));
  };

  const confirmCloseItem = () => {
      const { itemId, date, comment, image } = closeModal;
      // Das Datumsfeld laesst sich leeren. Eine Freimeldung ohne Datum ist im
      // Bericht eine Luecke — dann lieber wieder das Begehungsdatum.
      const closedDate = freimeldeDatum(date || reportDate);
      // Ueber denselben Helfer wie jeder andere Statuswechsel — das von Hand
      // gewaehlte Datum und die Freimelde-Felder gewinnen zuletzt.
      onChange(statusWechselAnwenden(items, itemId, 'closed', reportDate, {
          closedDate, statusComment: comment, closedImage: image,
      }));
      // REIHENFOLGE VERBINDLICH (docs/freimeldung-online-plan.md §F): erst den
      // Mangel im Client schliessen und SPEICHERN lassen, DANN die Meldung
      // buchen. Umgekehrt stuende die Meldung auf „bestaetigt", waehrend der
      // Mangel nach einem gescheiterten Save weiter offen ist — und niemand
      // wuerde ihn je wieder angeboten bekommen.
      //
      // Das `onChange` oben stoesst den Save erst an; er laeuft entprellt und
      // kann scheitern. Deshalb wird hier nur EINGEREIHT — das Begehungsmodul
      // bucht die Schlange erst ab, wenn es den Mangel in der Datenbank als
      // geschlossen wiedergefunden hat. Scheitert der Save, bleibt die Meldung
      // offen und ihr Abzeichen an der Karte: der sichere Zustand.
      if (closeModal.freimeldungId) {
          if (onFreimeldungNachSpeichern) {
              onFreimeldungNachSpeichern(closeModal.freimeldungId, itemId, 'bestaetigt');
          } else if (onFreimeldungEntscheiden) {
              void onFreimeldungEntscheiden(closeModal.freimeldungId, 'bestaetigt');
          }
      }
      setCloseModal(prev => ({ ...prev, isOpen: false }));
  };
  const closeModalSchliessen = () => setCloseModal(prev => ({ ...prev, isOpen: false }));

  const loeschItem = loeschen ? items.find(i => i.id === loeschen.id) : undefined;

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 animate-fade-in">
        
        <InspectionFilterBar 
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            filterMode={filterMode}
            itemCount={filteredItems.length}
            onAddItem={handleAddItem}
            onMobileAdd={onMobileAdd}
            onAiAssistGlobal={() => { setExpandedItem(null); setAiInitialText(''); setIsAiHelperOpen(true); }}
            language={language}
            onKatalog={katalogAktiv ? () => setKatalogZiel(KATALOG_NEU) : undefined}
            themen={themen}
            themaFilter={wirksamerThemaFilter}
            onThemaFilterChange={setThemaFilter}
        />

        <div className="space-y-4">
            {/* Am Handy ein weißes Listenblatt (Karten mit gruppiert: nur Haarlinien),
                am PC einzelne Karten. overflow-hidden schneidet die fixed-Einblendungen
                der Karten nicht ab, solange kein Vorfahr transform/filter trägt. */}
            <div className="max-md:bg-white max-md:rounded-xl max-md:overflow-hidden md:space-y-4">
                {/* Nur feste Handler und gemerkte Werte: jede Karte ist React.memo. */}
                {filteredItems.slice(0, sichtbareAnzahl).map((item, kartenIndex) => (
                    <InspectionItemCard
                        key={item.id}
                        gruppiert
                        tutorialErsteKarte={kartenIndex === 0}
                        item={item}
                        isExpanded={expandedItem === item.id}
                        onToggleExpand={aufklappenUmschalten}
                        onUpdate={handleUpdateItem}
                        onPatch={handlePatchItem}
                        onDelete={handleDeleteItem}
                        onStatusChange={handleStatusChangeTrigger}
                        onAddPhotos={handleAddPhotos}
                        onRemovePhoto={handleRemovePhoto}
                        onPinOnPlan={aufPlanVerorten}
                        onAiAssist={handleAiAssistDirect}
                        onKiRueckgaengig={kiRueckgaengigMoeglich(kiRueckgaengig, item) ? kiRueckgaengigAnwenden : undefined}
                        contacts={kartenKontakte}
                        language={language}
                        availablePlans={availablePlans}
                        freimeldungen={freimeldungen?.get(item.id)}
                        freimeldungFehler={freimeldungFehler}
                        onFreimeldungBestaetigen={onFreimeldungEntscheiden ? openCloseFromFreimeldung : undefined}
                        onFreimeldungAblehnen={onFreimeldungEntscheiden ? freimeldungAblehnen : undefined}
                        onFreimeldungKenntnis={onFreimeldungNachSpeichern ? freimeldungKenntnis : undefined}
                        onKatalog={katalogAktiv ? katalogFuerEintrag : undefined}
                        umgebung={kartenUmgebung}
                    />
                ))}
            </div>
            {restlicheKarten > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                    <Button variant="secondary" onClick={() => setFenster({ lage, anzahl: sichtbareAnzahl + KARTEN_SEITE })}>
                        {language === 'en' ? `Show more (${weitereKarten})` : `Weitere anzeigen (${weitereKarten})`}
                    </Button>
                    {/* „Alle“ bleibt alle, auch wenn die Liste danach waechst. */}
                    <Button variant="ghost" onClick={() => setFenster({ lage, anzahl: Number.POSITIVE_INFINITY })}>
                        {language === 'en' ? 'Show all' : 'Alle anzeigen'}
                    </Button>
                    <p className="w-full text-center text-xs text-slate-500">
                        {language === 'en'
                            ? `${sichtbareAnzahl} of ${filteredItems.length} shown`
                            : `${sichtbareAnzahl} von ${filteredItems.length} angezeigt`}
                    </p>
                </div>
            )}
        </div>

        {/* --- MODALS --- */}
        <AIHelper isOpen={isAiHelperOpen} onClose={() => setIsAiHelperOpen(false)} onApply={handleAIApply} language={language} appMode={appMode} initialText={aiInitialText} />

        {/* STANDARDMANGEL (Katalog-Dialog, Portal an body) */}
        <MangelKatalogDialog
            offen={katalogAktiv && katalogZiel !== null}
            titel={katalogDialogTitel(katalogZiel, items, erfassungsSprache)}
            startPfad={katalogStartPfad}
            onAuswahl={handleKatalogAuswahl}
            onSchliessen={() => setKatalogZiel(null)}
            sprache={erfassungsSprache}
        />

        {loeschItem && loeschen && (
            <MangelLoeschenDialog
                item={loeschItem}
                entscheid={loeschen.entscheid}
                language={language}
                onAbbrechen={loeschenAbbrechen}
                onVerwerfen={() => { void loeschenVerwerfen(loeschen.id, loeschen.entscheid?.art === 'unbekannt'); }}
                onErledigt={() => { const id = loeschen.id; loeschenAbbrechen(); handleStatusChangeTrigger(id, 'closed'); }}
            />
        )}

        {/* CLOSE MODAL */}
        {closeModal.isOpen && (
            <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                {/* Querformat am Handy: nur der Inhalt scrollt, Kopf und Knoepfe bleiben sichtbar. */}
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90dvh] flex flex-col overflow-hidden animate-scale-in">
                    <div className="shrink-0 bg-green-600 text-white p-4 flex justify-between items-center">
                        <h3 className="font-bold flex items-center gap-2"><CheckCircle className="w-5 h-5"/> {language === 'en' ? 'Close Deficiency' : 'Mangel erledigen'}</h3>
                        <button onClick={closeModalSchliessen} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5"/></button>
                    </div>
                    {/* Aenderungen funktional: ein Beweisbild, das zwischendurch ankommt, bleibt stehen. */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">{language === 'en' ? 'Clearance Date' : 'Datum der Freimeldung'}</label>
                            <input data-tutorial="mangel.freimeldung-datum" type="date" value={closeModal.date} onChange={(e) => { const date = e.target.value; setCloseModal(prev => ({ ...prev, date })); }} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 outline-none"/>
                            {/* Bei Nacharbeit steht hier ein Datum in der Vergangenheit. Ohne
                                Hinweis wirkt das wie ein Fehler — oder es bleibt stehen,
                                obwohl die Firma erst spaeter per Foto freigemeldet hat. */}
                            <p className="text-[11px] text-slate-400 mt-1">
                                {language === 'en'
                                    ? 'Preset: date of the inspection. Change it if the defect was cleared on a different day.'
                                    : 'Vorbelegt mit dem Datum der Begehung. Wurde später behoben? Datum einfach ändern.'}
                            </p>
                        </div>
                        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">{language === 'en' ? 'Clearance / Comment' : 'Freimeldung / Kommentar'}</label><textarea data-tutorial="mangel.freimeldung-kommentar" rows={3} value={closeModal.comment} onChange={(e) => { const comment = e.target.value; setCloseModal(prev => ({ ...prev, comment })); }} className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 outline-none text-sm" placeholder={language === 'en' ? 'e.g. Handrail has been installed.' : 'z.B. Geländer wurde montiert.'}/></div>
                        <div>
                            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">{language === 'en' ? 'Photo Proof (Optional)' : 'Beweisbild (Optional)'}</label>
                            <div className="flex gap-3 items-center">
                                {closeModal.image ? (
                                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200"><img src={closeModal.image} alt="Beweis" className="w-full h-full object-cover" /><button onClick={() => setCloseModal(prev => ({ ...prev, image: undefined }))} className="absolute top-0 right-0 bg-red-500 text-white p-0.5"><X className="w-3 h-3" /></button></div>
                                ) : beweisbildLaedt ? (
                                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
                                ) : kameraUndAlbum ? (
                                    <>
                                        <button type="button" onClick={() => closeKameraInputRef.current?.click()} className="w-16 h-16 shrink-0 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-0.5 text-slate-400 hover:border-green-400 hover:text-green-500 transition-colors"><Camera className="w-5 h-5" /><span className="text-[11px] font-semibold">{language === 'en' ? 'Camera' : 'Kamera'}</span></button>
                                        <button type="button" onClick={() => closeImageInputRef.current?.click()} className="w-16 h-16 shrink-0 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-0.5 text-slate-400 hover:border-green-400 hover:text-green-500 transition-colors"><ImagePlus className="w-5 h-5" /><span className="text-[11px] font-semibold">Album</span></button>
                                    </>
                                ) : (
                                    <button onClick={() => closeImageInputRef.current?.click()} className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-green-400 hover:text-green-500 transition-colors"><ImagePlus className="w-5 h-5" /></button>
                                )}
                                <div className="text-xs text-slate-400 flex-1" aria-live="polite">
                                    {beweisbildLaedt
                                        ? (language === 'en' ? 'Preparing image …' : 'Bild wird vorbereitet …')
                                        : (language === 'en' ? 'Upload a photo that proves the completion. Will be shown once in the report.' : 'Laden Sie ein foto hoch, das die Erledigung belegt. Wird einmalig im Bericht angezeigt.')}
                                </div>
                            </div>
                            <input 
                              type="file" 
                              ref={closeImageInputRef} 
                              accept={`image/*,${HEIC_ACCEPT}`}
                              className="hidden" 
                              onChange={handleCloseImageUpload} 
                            />
                            {/* Wie an der Mangelkarte: `capture` öffnet direkt die Kamera. */}
                            {kameraUndAlbum && <input type="file" ref={closeKameraInputRef} accept="image/*" capture="environment" className="hidden" onChange={handleCloseImageUpload} />}
                        </div>
                    </div>
                    {/* „Abschließen“ wartet, bis das Beweisbild verkleinert ist — sonst ginge es still verloren. Aufs Hochladen wartet es nicht. */}
                    <div className="shrink-0 p-4 bg-slate-50 border-t flex justify-end gap-3"><Button variant="ghost" onClick={closeModalSchliessen}>{language === 'en' ? 'Cancel' : 'Abbrechen'}</Button><Button data-tutorial="mangel.freimeldung-abschliessen" variant="primary" onClick={confirmCloseItem} disabled={beweisbildLaedt} className="bg-green-600 hover:bg-green-700">{language === 'en' ? 'Finish' : 'Abschließen'}</Button></div>
                </div>
            </div>
        )}

        {/* VERORTUNG AUF PLAN — Dialog bleibt offen bis „Fertig“/Esc/X; jede Aenderung gilt sofort. */}
        {verortungItem && (
            <PlanVerortungDialog
                key={verortungItem.id}
                plaene={verortungPlaene}
                pins={pinsVon(verortungItem)}
                nummer={verortungItem.ticketNumber}
                onPinSetzen={pinSetzen}
                onPinEntfernen={pinEntfernenAmEintrag}
                onFertig={() => setVerortungItemId(null)}
                planAnlegen={planAnlegen}
                live={live}
                onLiveUebernehmen={liveUebernehmen}
                // Tipp auf den Plan eines Geschosses = Korrektur „hier bin ich“ (neuer Nullpunkt).
                onPlanGetippt={live.planBestaetigen}
                language={language === 'en' ? 'en' : 'de'}
            />
        )}
    </div>
  );
};
